import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import useAuthStore, { PERMISSIONS } from './stores/authStore';
import useThemeStore from './stores/themeStore';

// Layout
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import CafeRegistry from './pages/CafeRegistry';
import CafeRemote from './pages/CafeRemote';
import CafePcDetails from './pages/CafePcDetails';
import CafeEdit from './pages/CafeEdit';
import Subscriptions from './pages/Subscriptions';
import Analytics from './pages/Analytics';
import AdminActivity from './pages/AdminActivity';
import MIS from './pages/MIS';
import TeamProgress from './pages/TeamProgress';
import SystemHealth from './pages/SystemHealth';
import UserManagement from './pages/UserManagement';
import AuditLogs from './pages/AuditLogs';
import Settings from './pages/Settings';
// Capability scaffolds (visible surface for design handoff)
import PCRegistry from './pages/PCRegistry';
import PCActiveBreakdown from './pages/PCActiveBreakdown';
import CafeOnboard from './pages/CafeOnboard';
import Licenses from './pages/Licenses';
import Billing from './pages/Billing';
import RemoteCommands from './pages/RemoteCommands';
import Announcements from './pages/Announcements';
import SupportTickets from './pages/SupportTickets';
import CustomerFeedback from './pages/CustomerFeedback';
import RolesPermissions from './pages/RolesPermissions';
import FeatureFlags from './pages/FeatureFlags';
import CrossTenantSearch from './pages/CrossTenantSearch';

// /cafes/:id/details was the café Details page before it became the café's
// main page. Kept as a redirect for good: Teams alert cards already posted
// carry that address. ?pc= (the PC to highlight) goes along with it.
function CafeDetailsRedirect() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/cafes/${id}${search}`} replace />;
}

function App() {
  const { restoreSession } = useAuthStore();
  const { initializeTheme } = useThemeStore();

  useEffect(() => {
    restoreSession();
    initializeTheme();
  }, [restoreSession, initializeTheme]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />

        {/* Protected routes with layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* MIS — every KPI (Daily / Weekly / Monthly) plus the player and session
              detail. Same access as the dashboard itself. */}
          <Route path="/mis" element={<MIS />} />

          {/* Team progress — product work from Jira, for management. Same
              access as MIS (the backend admits the same portal accounts). */}
          <Route path="/team-progress" element={<TeamProgress />} />

          {/* Café routes */}
          <Route
            path="/cafes"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_CAFE_REGISTRY}>
                <CafeRegistry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cafes/new"
            element={
              <ProtectedRoute permission={PERMISSIONS.EDIT_CAFE_DETAILS}>
                <CafeOnboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cafes/:id"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_CAFE_REGISTRY}>
                <CafePcDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cafes/:id/remote"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_CAFE_REGISTRY}>
                <CafeRemote />
              </ProtectedRoute>
            }
          />
          <Route path="/cafes/:id/details" element={<CafeDetailsRedirect />} />
          <Route
            path="/cafes/:id/edit"
            element={
              <ProtectedRoute permission={PERMISSIONS.EDIT_CAFE_DETAILS}>
                <CafeEdit />
              </ProtectedRoute>
            }
          />

          {/* Subscriptions */}
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_SUBSCRIPTIONS}>
                <Subscriptions />
              </ProtectedRoute>
            }
          />

          {/* Analytics */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_FINANCIAL_ANALYTICS}>
                <Analytics />
              </ProtectedRoute>
            }
          />

          {/* Admin Activity */}
          <Route
            path="/admin-activity"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_ADMIN_ACTIVITY}>
                <AdminActivity />
              </ProtectedRoute>
            }
          />

          {/* System Health */}
          <Route
            path="/system-health"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_PC_HEALTH}>
                <SystemHealth />
              </ProtectedRoute>
            }
          />

          {/* User Management */}
          <Route
            path="/users"
            element={
              <ProtectedRoute permission={[PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_USERS]}>
                <UserManagement />
              </ProtectedRoute>
            }
          />

          {/* Audit Logs */}
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_AUDIT_LOGS}>
                <AuditLogs />
              </ProtectedRoute>
            }
          />

          {/* Licensing & Billing */}
          <Route
            path="/licenses"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_SUBSCRIPTIONS}>
                <Licenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_SUBSCRIPTIONS}>
                <Billing />
              </ProtectedRoute>
            }
          />

          {/* Fleet */}
          <Route
            path="/pcs"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_PC_HEALTH}>
                <PCRegistry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pcs/active-breakdown"
            element={
              <ProtectedRoute permission={PERMISSIONS.VIEW_PC_HEALTH}>
                <PCActiveBreakdown />
              </ProtectedRoute>
            }
          />
          <Route
            path="/commands"
            element={
              <ProtectedRoute permission={PERMISSIONS.EXECUTE_PC_COMMANDS}>
                <RemoteCommands />
              </ProtectedRoute>
            }
          />

          {/* Communications */}
          <Route path="/announcements" element={<Announcements />} />
          <Route path="/support" element={<SupportTickets />} />
          <Route path="/customer-feedback" element={<CustomerFeedback />} />

          {/* Access control */}
          <Route
            path="/roles"
            element={
              <ProtectedRoute permission={PERMISSIONS.MANAGE_PERMISSIONS}>
                <RolesPermissions />
              </ProtectedRoute>
            }
          />

          {/* Planned capabilities (no backend yet) */}
          <Route path="/feature-flags" element={<FeatureFlags />} />
          <Route path="/cross-tenant-search" element={<CrossTenantSearch />} />

          {/* Settings */}
          <Route path="/settings" element={<Settings />} />
        </Route>

        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="not-found">
              <h1>404</h1>
              <p>Page not found</p>
              <a href="/dashboard" className="btn btn--secondary">
                Back to Dashboard
              </a>
              <style>{`
                .not-found {
                  min-height: 100vh;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  text-align: center;
                  gap: var(--space-md);
                  background: var(--bg-page);
                }
                .not-found h1 {
                  font-size: 6rem;
                  font-weight: 700;
                  color: var(--text-primary);
                }
                .not-found p {
                  color: var(--text-tertiary);
                  font-size: var(--text-lg);
                }
              `}</style>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
